/**
 * @Description 发起点位评估审批弹框
 *  2024/12/05 目前看来三套任务模板处理无差异，统一为同一个文件降低迭代成本，后续看情况处理
 *  2025/1/13 益禾堂增加上传美团报告，通过可选入参isYihetang区分是否需要处理
 */
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Modal } from 'antd';
import { useMethods } from '@lhb/hook';
import V2Form from '@/common/components/Form/V2Form';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import { createApproval } from '@/common/api/expandStore/approveworkbench'; // 提交审批
import styles from './index.module.less';
import { dealApprovalSumitExtras } from '@/common/components/business/ExpandStore/ApprovalSelectNodes/config';
import ApprovalSelectNodes from '@/common/components/business/ExpandStore/ApprovalSelectNodes';
import { getChancepointAllFileds, getFieldWithIdentification } from '@/common/components/business/DynamicComponent/chancepointTools';
import { isNotEmptyAny, isArray } from '@lhb/func';
import MeituanReport from './components/MeituanReport';
import { ControlType, parseValueCatch, uploadValToJSON } from '../../../DynamicComponent/config';
import { SavePropertyValues } from './ts-config';
import EstimateSalesOfYht from './EstimateSalesOfYht';
import { meituanReportYHTIdentification } from '../../../DynamicComponent/customize';

interface Props {
	approvalModal;
	setApprovalModal;
  isYihetang?: boolean;
  refresh;
}

const CreateSpotApproval: FC<Props> = ({
  approvalModal,
  setApprovalModal,
  isYihetang = false,
  refresh
}) => {
  const [form] = Form.useForm(); // 表单参数
  const [mtProperty, setMtProperty] = useState<any>({});
  const [estimateSale, setEstimateSale] = useState<any>(null);
  const isLockRef = useRef<boolean>(false);

  // 请求审批人选项配置时的额外参数（配置了机会点点位审批流程时需要传evaluationFlowId）
  const configExtraParams = approvalModal?.evaluationFlowId ? { evaluationFlowId: approvalModal.evaluationFlowId } : {};

  useEffect(() => {
    if (isYihetang && approvalModal.show && approvalModal.id) {
      getChancePointFileds(approvalModal.id);
    }
  }, [approvalModal.show]);

  useEffect(() => {
    if (isNotEmptyAny(mtProperty)) {
      const val = parseValueCatch(mtProperty) || [];
      form.setFieldValue(mtProperty.identification, val);
    }
  }, [mtProperty]);

  /**
   * @description 是否需要展示美团报告
   */
  const showUploadFile = useMemo(() => {
    return isYihetang && isNotEmptyAny(mtProperty);
  }, [isYihetang, mtProperty]);

  const getChancePointFileds = async (chanceId: number) => {
    const res = await getChancepointAllFileds(chanceId);
    // 美团报告
    const mtreportProperty = getFieldWithIdentification(res, meituanReportYHTIdentification);
    setMtProperty(isNotEmptyAny(mtreportProperty) ? mtreportProperty : {});

    // 竞品均值
    const props = res.filter((itm) => ([
      ControlType.ESTIMATE_SALES_MT_MEAN_YHT.value,
      ControlType.ESTIMATE_SALES_MT_MAX_YHT.value,
    ].includes(itm.controlType)));
    setEstimateSale((isArray(props) && props.length) ? props : null); // 无数据直接设置null方便判断
  };

  const methods = useMethods({
    /** 提交审批 */
    onOk() {
      form
        .validateFields()
        .then(async (values: any) => {
          if (isLockRef.current) return;

          const extras: any = dealApprovalSumitExtras(values);

          // 动态字段
          const propertyValues: SavePropertyValues[] = [];

          // 美团报告
          if (showUploadFile) {
            const attachments = values[mtProperty.identification];
            if (isArray(attachments) && attachments.length) {
              let uploadIsComplete = true;
              attachments.forEach((uploadComItem: any) => {
                const { url } = uploadComItem;
                if (!url) {
                  uploadIsComplete = false;
                }
              });
              if (!uploadIsComplete) {
                V2Message.warning('文件上传中...');
                return;
              };
            }
            propertyValues.push({
              id: mtProperty.id,
              propertyId: mtProperty.propertyId,
              identification: mtProperty.identification,
              textValue: uploadValToJSON(attachments),
            });
          }
          if (estimateSale) {
            // 美团竞品均值
            const mtMeanCom = estimateSale.find((item) => item.controlType === ControlType.ESTIMATE_SALES_MT_MEAN_YHT.value);
            mtMeanCom && (propertyValues.push({
              id: mtMeanCom.id,
              propertyId: mtMeanCom.propertyId,
              identification: mtMeanCom.identification,
              textValue: values[mtMeanCom.identification]
            }));
            // 美团竞品高值
            const mtMaxCom = estimateSale.find((item) => item.controlType === ControlType.ESTIMATE_SALES_MT_MAX_YHT.value);
            mtMaxCom && (propertyValues.push({
              id: mtMaxCom.id,
              propertyId: mtMaxCom.propertyId,
              identification: mtMaxCom.identification,
              textValue: values[mtMaxCom.identification],
            }));
          }
          const { id, type, typeValue, evaluationFlowId } = approvalModal;
          isLockRef.current = true;
          createApproval({
            id,
            type,
            typeValue,
            evaluationFlowId, // 机会点点位评估审批流ID
            reason: values.reason,
            ...extras,
            ...(propertyValues.length ? { directSavePropertyValues: propertyValues } : {}),
            ...(approvalModal?.evaluationFlowId ? { propertyValues: approvalModal.propertyValues } : {}),
            ...(approvalModal?.standardTaskId ? { standardTaskId: approvalModal.standardTaskId } : {})
          }).then(() => {
            V2Message.success('提交审批成功');
            // bigdataBtn('ef41f35d-73eb-944f-761b-72adafee2074', '拓店任务', '提交点位评估', `点击了提交点位评估`);
            form.resetFields();
            setApprovalModal({
              show: false,
              id: undefined,
              type: undefined,
              typeValue: undefined,
            });
            refresh();
          }).finally(() => {
            isLockRef.current = false;
          });
        })
        .catch((error) => {
          console.error('提交审批失败:', error);
        });
    },

    /** 点击取消 */
    onCancel() {
      form.resetFields();
      setApprovalModal({
        show: false,
        id: undefined,
        type: undefined,
        typeValue: undefined,
      });
    },
  });
  return (
    <>
      <Modal
        title='提交审批'
        open={approvalModal.show}
        onOk={methods.onOk}
        // 两列弹窗要求336px
        width={isYihetang ? 480 : 336}
        onCancel={methods.onCancel}
        forceRender
        className={styles.createModal}
        zIndex={1001}
      >
        <V2Form form={form}>
          <ApprovalSelectNodes
            id={approvalModal.id}
            type={approvalModal.type}
            typeValue={approvalModal.typeValue}
            configExtraParams={configExtraParams}
          />
          <V2FormTextArea
            required
            maxLength={500}
            label='发起理由'
            name='reason'
            config={{ showCount: true }}
          />
          {showUploadFile ? <MeituanReport
            propertyItem={mtProperty}
          /> : <></>}
          {estimateSale ? <EstimateSalesOfYht
            propertyItems={estimateSale}
            form={form}
          /> : <></>}
        </V2Form>
      </Modal>
    </>
  );
};
export default CreateSpotApproval;
